import { memo, Suspense, useMemo, useCallback, useEffect } from 'react';

import type { RelevancyCardProps } from '../DynamicCardComponent';
import { usePollGame } from '../hooks/usePollGame';
import { getComponent, useRelevancyFeed } from '../model';
import { useRelevancyOptions } from '../store';
import type { RelevancyCard } from '../types';
import { RelevancyCardType } from '../types';

import type { EditorialContent, MediaImage } from '@cobalt/utilities';
import { resolvePathUrl } from '@cobalt/utilities';
import Loader from '@shared/Loader';

type FspEditorialContent = EditorialContent & {
  kind: string;
  section: string;
  mediaType: string;
  images: {
    size: string;
    src: string;
  }[];
};

const getNewMainMedia = (images: FspEditorialContent['images']): Partial<MediaImage> => {
  if (!images) return null;

  return {
    image: {
      wide: {
        w1: resolvePathUrl(images.find((i) => i.size === 'WIDE')?.src, 'wide1'),
      },
      square: {
        s1: resolvePathUrl(images.find((i) => i.size === 'SQUARE')?.src, 'square1'),
      },
      portrait: {
        p1: resolvePathUrl(images.find((i) => i.size === 'PORTRAIT')?.src, 'portrait1'),
      },
    },
  };
};

const skillKinds = [
  'Skills',
  'Moments',
  'Saves',
  'Assists',
  'Goal of the Day',
  'Trendsetters',
  'Teamwork',
  'Well Travelled',
  'Talents',
  'European Adventures',
  'Video Show Clips',
] as const;

const EidosComponent = ({ item, index, cardFormat }: RelevancyCardProps) => {
  const { setCardError } = useRelevancyFeed();
  const { options } = useRelevancyOptions();

  const teaser: FspEditorialContent = useMemo(() => {
    if (!item.teaser) return null;

    const mainMedia = item.teaser.mainMedia ? item.teaser.mainMedia : getNewMainMedia(item.teaser.images);

    return {
      ...item.teaser,
      mainMedia,
    };
  }, [item.teaser]);

  const kind = useMemo(() => teaser?.classification?.kind ?? teaser?.kind ?? item.subType, [item.subType, teaser]);
  const section = useMemo(() => teaser?.classification?.section ?? teaser?.section, [teaser]);
  const itemType = useMemo(() => teaser?.type ?? teaser?.mediaType?.toLowerCase(), [teaser]);

  const { mapPollGame, isPollGame } = usePollGame(options, section);

  const getSubtype = useCallback(() => {
    if (isPollGame) return 'GOTX-HOME-CARD';
    else if (item.subType) return item.subType;
    else if (kind) return kind.replaceAll('_', '-').replaceAll(' ', '-');
  }, [isPollGame, kind, item.subType]);

  const type: RelevancyCardType = useMemo(() => {
    const itemTypeMap = {
      article: () => {
        if (section === '3rd Parties/TOTW') return RelevancyCardType.TEAM_OF_THE_WEEK;
        if (kind === 'Players to Watch') return RelevancyCardType.PLAYERS_TO_WATCH;
        if (kind === 'Quizarena') return RelevancyCardType.QUIZARENA;
        if (kind === 'AudioStream') return RelevancyCardType.LIVE_AUDIO;
        if (kind === 'Podcasts') return RelevancyCardType.PODCAST;
        if (section === 'Editorial/Topscorer' || teaser?.tag?.includes('.CARD.TOPSCORER'))
          return RelevancyCardType.TOP_SCORER;
        if (teaser?.tag?.includes('.CARD.PERFORMANCEZONE')) return RelevancyCardType.PERFORMANCE_ZONE;
        return RelevancyCardType.ARTICLE;
      },
      promo: () => {
        if (section === '3rd Parties/TOTW') return RelevancyCardType.TEAM_OF_THE_WEEK;
        if (kind === 'Players to Watch') return RelevancyCardType.PLAYERS_TO_WATCH;
        if (kind === 'Quizarena') return RelevancyCardType.QUIZARENA;
        if (kind === 'AudioStream') return RelevancyCardType.LIVE_AUDIO;
        if (kind === 'Podcasts') return RelevancyCardType.PODCAST;
        if (section === 'Editorial/Topscorer' || teaser?.tag?.includes('.CARD.TOPSCORER'))
          return RelevancyCardType.TOP_SCORER;
        if (teaser?.tag?.includes('.CARD.PERFORMANCEZONE')) return RelevancyCardType.PERFORMANCE_ZONE;
        return RelevancyCardType.ARTICLE;
      },
      video: () => {
        if (isPollGame) {
          return RelevancyCardType.GAMING;
        }
        if (skillKinds.includes(kind)) return RelevancyCardType.SKILLS_GROUP_EIDOS;
        return RelevancyCardType.VIDEO;
      },
      videostory: () => {
        if (isPollGame) {
          return RelevancyCardType.GAMING;
        }
        if (skillKinds.includes(kind)) return RelevancyCardType.SKILLS_GROUP_EIDOS;
        return RelevancyCardType.VIDEO;
      },
      gallery: () => RelevancyCardType.PHOTO_GALLERY,
      photostory: () => {
        if (isPollGame) return RelevancyCardType.GAMING;
        if (section === 'Editorial/Topscorer' || teaser?.tag?.includes('.CARD.TOPSCORER'))
          return RelevancyCardType.TOP_SCORER;
        if (teaser?.tag?.includes('.CARD.PERFORMANCEZONE')) return RelevancyCardType.PERFORMANCE_ZONE;
        return null;
      },
      document: () => RelevancyCardType.DOCUMENT,
    } as const;
    return itemTypeMap[itemType]?.();
  }, [itemType, isPollGame, kind, section, teaser?.tag]);

  const mappedItem: RelevancyCard = useMemo(
    () => ({
      ...item,
      teaser: isPollGame ? mapPollGame(section) : teaser,
      subType: getSubtype(),
    }),
    [item, isPollGame, mapPollGame, section, teaser, getSubtype],
  );

  const Component = useMemo(
    () => getComponent<RelevancyCardProps & { eidosType: string }>(type, options.Components),
    [options.Components, type],
  );

  useEffect(() => {
    !Component && setCardError(item, `Component ${itemType} doesn't exists`);
  }, [Component, itemType, item, setCardError]);

  if (!Component) return null;

  return (
    <>
      {Component && (
        <Suspense fallback={<Loader />}>
          <Component item={mappedItem} eidosType={itemType} index={index} cardFormat={cardFormat} />
        </Suspense>
      )}
    </>
  );
};

export default memo(EidosComponent);
